import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Collapse,
  Grid,
  IconButton,
  InputAdornment,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
  Alert,
  Avatar,
  Divider,
} from '@mui/material';
import {
  Add as AddIcon,
  Search as SearchIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Inventory as InventoryIcon,
  QrCode as QrCodeIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { getProductsPaginated } from '../../product/services/productService';
import { fetchVariantsByProduct, removeVariant } from '../../../app/store/slices/productVariantSlice';
import VariantFormDialog from '../components/VariantFormDialog';
import ConfirmationDialog from '../../../components/ConfirmationDialog';
import { useModulePermissions } from '../../../hooks/useModulePermissions';

const PRODUCT_VARIANTS_ENABLED = false;

// ─── Variant Card ─────────────────────────────────────────────────────────────
const VariantCard = ({ variant, onEdit, onDelete, canEdit, canDelete }) => {
  const stockColor =
    variant.availableStock <= 0
      ? 'error'
      : variant.availableStock <= (variant.minStockLevel || 5)
      ? 'warning'
      : 'success';

  return (
    <Card
      variant="outlined"
      sx={{
        borderRadius: 2,
        transition: 'box-shadow 0.2s',
        '&:hover': { boxShadow: 3 },
      }}
    >
      <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
          {/* Left: avatar + name */}
          <Stack direction="row" spacing={1.5} alignItems="center" sx={{ flexGrow: 1, minWidth: 0 }}>
            <Avatar
              src={variant.imageUrl || undefined}
              sx={{ width: 44, height: 44, bgcolor: 'primary.light', fontSize: 18 }}
            >
              {!variant.imageUrl && (variant.variantName?.[0] || 'V')}
            </Avatar>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="body1" fontWeight={600} noWrap>
                {variant.variantName}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                SKU: {variant.sku}
              </Typography>
            </Box>
          </Stack>

          {/* Actions */}
          {(canEdit || canDelete) && (
            <Stack direction="row" spacing={0.5}>
              {canEdit && (
                <Tooltip title="Edit Variant">
                  <IconButton size="small" onClick={() => onEdit(variant)}>
                    <EditIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
              {canDelete && (
                <Tooltip title="Delete Variant">
                  <IconButton size="small" color="error" onClick={() => onDelete(variant)}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </Stack>
          )}
        </Stack>

        <Divider sx={{ my: 1.5 }} />

        {/* Metrics row */}
        <Grid container spacing={1}>
          <Grid item xs={6} sm={3}>
            <Typography variant="caption" color="text.secondary" display="block">Sale Price</Typography>
            <Typography variant="body2" fontWeight={600}>€{Number(variant.salePrice || 0).toFixed(2)}</Typography>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Typography variant="caption" color="text.secondary" display="block">Cost Price</Typography>
            <Typography variant="body2" fontWeight={600}>€{Number(variant.costPrice || 0).toFixed(2)}</Typography>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Typography variant="caption" color="text.secondary" display="block">Stock</Typography>
            <Chip
              size="small"
              label={`${variant.availableStock ?? variant.stockQuantity ?? 0} units`}
              color={stockColor}
              icon={stockColor === 'success' ? <CheckCircleIcon /> : <WarningIcon />}
            />
          </Grid>
          <Grid item xs={6} sm={3}>
            <Typography variant="caption" color="text.secondary" display="block">Type</Typography>
            {variant.isSerializedProduct ? (
              <Chip size="small" label="IMEI Tracked" color="info" icon={<QrCodeIcon />} />
            ) : (
              <Chip size="small" label="Standard" variant="outlined" />
            )}
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
};

// ─── Product Row ──────────────────────────────────────────────────────────────
const ProductRow = ({ product, canEdit, canDelete }) => {
  const dispatch = useDispatch();
  const [expanded, setExpanded] = useState(false);
  const [variantDialogOpen, setVariantDialogOpen] = useState(false);
  const [selectedVariant, setSelectedVariant] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);

  const variantsData = useSelector((s) => s.productVariants.variantsByProduct[product.Id]);
  const { loading, submitting } = useSelector((s) => s.productVariants);

  const handleExpand = () => {
    if (!expanded && !variantsData) {
      dispatch(fetchVariantsByProduct(product.Id));
    }
    setExpanded((v) => !v);
  };

  const handleEdit = (variant) => {
    setSelectedVariant(variant);
    setVariantDialogOpen(true);
  };

  const handleAdd = () => {
    setSelectedVariant(null);
    setVariantDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!confirmDelete) return;
    try {
      await dispatch(removeVariant({ id: confirmDelete.id, productId: product.Id })).unwrap();
      toast.success('Variant deleted');
    } catch (e) {
      toast.error(e || 'Delete failed');
    } finally {
      setConfirmDelete(null);
    }
  };

  const variants = variantsData?.variants || [];
  const totalStock = variants.reduce((s, v) => s + (v.availableStock ?? v.stockQuantity ?? 0), 0);

  return (
    <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden', mb: 1.5 }}>
      {/* Header row */}
      <Stack
        direction="row"
        alignItems="center"
        spacing={2}
        sx={{ px: 2, py: 1.5, cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' } }}
        onClick={handleExpand}
      >
        <Avatar src={product.ImageUrl || undefined} sx={{ width: 40, height: 40, bgcolor: 'secondary.light' }}>
          {!product.ImageUrl && (product.Name?.[0] || 'P')}
        </Avatar>
        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
          <Typography variant="body1" fontWeight={600} noWrap>{product.Name}</Typography>
          <Typography variant="caption" color="text.secondary">SKU: {product.SKU}</Typography>
        </Box>
        <Stack direction="row" spacing={1} alignItems="center">
          <Chip
            size="small"
            label={`${variantsData ? variants.length : '...'} variants`}
            color={variants.length > 0 ? 'primary' : 'default'}
            variant="outlined"
          />
          <Chip
            size="small"
            label={`Stock: ${variantsData ? totalStock : '...'}`}
            color={totalStock > 0 ? 'success' : 'default'}
            variant="outlined"
            icon={<InventoryIcon />}
          />
        </Stack>
        {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
      </Stack>

      <Collapse in={expanded} unmountOnExit>
        <Divider />
        <Box sx={{ p: 2 }}>
          {loading && variants.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 3 }}>
              <CircularProgress size={24} />
            </Box>
          ) : (
            <>
              <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1.5}>
                <Typography variant="subtitle2" color="text.secondary">Variants</Typography>
                {canEdit && (
                  <Button
                    size="small"
                    startIcon={<AddIcon />}
                    variant="contained"
                    onClick={(e) => { e.stopPropagation(); handleAdd(); }}
                  >
                    Add Variant
                  </Button>
                )}
              </Stack>

              {variants.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 3, color: 'text.secondary' }}>
                  <InventoryIcon sx={{ fontSize: 40, mb: 1, opacity: 0.3 }} />
                  <Typography variant="body2">No variants yet. Add the first one.</Typography>
                </Box>
              ) : (
                <Grid container spacing={1.5}>
                  {variants.map((v) => (
                    <Grid item xs={12} md={6} key={v.id}>
                      <VariantCard
                        variant={v}
                        onEdit={handleEdit}
                        onDelete={setConfirmDelete}
                        canEdit={canEdit}
                        canDelete={canDelete}
                      />
                    </Grid>
                  ))}
                </Grid>
              )}
            </>
          )}
        </Box>
      </Collapse>

      {/* Dialogs */}
      <VariantFormDialog
        open={variantDialogOpen}
        onClose={() => setVariantDialogOpen(false)}
        productId={product.Id}
        variant={selectedVariant}
        onSaved={() => dispatch(fetchVariantsByProduct(product.Id))}
      />

      <ConfirmationDialog
        open={Boolean(confirmDelete)}
        title="Delete Variant"
        message={`Delete variant "${confirmDelete?.variantName}"? This cannot be undone.`}
        onConfirm={handleDeleteConfirm}
        onCancel={() => setConfirmDelete(null)}
        loading={submitting}
      />
    </Paper>
  );
};

// ─── Main Page ────────────────────────────────────────────────────────────────
const ProductVariantsPage = () => {
  const { t } = useTranslation();
  const { canViewModule, canEditModule, canCreateModule, canDeleteModule } = useModulePermissions();

  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const PAGE_SIZE = 20;

  const canEdit = canEditModule('Products');
  const canCreate = canCreateModule('Products');
  const canDelete = canDeleteModule('Products');

  const loadProducts = useCallback(async (reset = false) => {
    if (!PRODUCT_VARIANTS_ENABLED) return;

    try {
      setLoading(true);
      const currentPage = reset ? 1 : page;
      const res = await getProductsPaginated({
        pageNumber: currentPage,
        pageSize: PAGE_SIZE,
        searchTerm: searchQuery || null,
        sortBy: 'Name',
        sortDirection: 'Asc',
      });
      const raw = res?.payload?.items ?? res?.payload ?? res?.data?.items ?? res?.data ?? res ?? [];
      const items = Array.isArray(raw) ? raw : [];
      if (reset) {
        setProducts(items);
        setPage(2);
      } else {
        setProducts((prev) => [...prev, ...items]);
        setPage((p) => p + 1);
      }
      setHasMore(items.length === PAGE_SIZE);
    } catch {
      toast.error('Failed to load products');
    } finally {
      setLoading(false);
    }
  }, [searchQuery, page]);

  // Initial load
  useEffect(() => {
    loadProducts(true);
  }, [searchQuery, loadProducts]);

  if (!PRODUCT_VARIANTS_ENABLED) {
    return (
      <Box sx={{ p: { xs: 2, sm: 3 } }}>
        <Alert severity="info">Product variants are currently disabled.</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      {/* Header */}
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h5" fontWeight={700}>Product Variants</Typography>
          <Typography variant="body2" color="text.secondary">
            Manage variants (color, size, storage) for your products. Enable IMEI tracking per variant.
          </Typography>
        </Box>
      </Stack>

      {/* Search */}
      <TextField
        fullWidth
        placeholder="Search products..."
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon color="action" />
            </InputAdornment>
          ),
        }}
        sx={{ mb: 3, maxWidth: 480 }}
      />

      {/* Product list */}
      {loading && products.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      ) : products.length === 0 ? (
        <Alert severity="info">No products found matching your search.</Alert>
      ) : (
        <>
          {products.map((product) => (
            <ProductRow
              key={product.Id}
              product={product}
              canEdit={canEdit || canCreate}
              canDelete={canDelete}
            />
          ))}

          {hasMore && (
            <Box sx={{ textAlign: 'center', mt: 2 }}>
              <Button onClick={() => loadProducts(false)} disabled={loading}>
                {loading ? <CircularProgress size={20} /> : 'Load More'}
              </Button>
            </Box>
          )}
        </>
      )}
    </Box>
  );
};

export default ProductVariantsPage;
